import { CuboidPartsArrays } from '@App/app/entities/layer/cuboid.model';
import {
  LAYER_TYPES,
  RECT_TUBE_PART,
  RECT_TUBE_STRICT,
} from '@App/app/entities/layer/enums/layer-types.enum';
import { Injectable } from '@angular/core';
import { Matrix, Mesh, Plane, Quaternion } from 'babylonjs';
import { median } from 'src/app/shared/utils/utils';
import { BabylonNodesService } from '../../../babylon-nodes-service/babylon-nodes.service';
import { UtilsService } from '../../../utils-service/utils.service';
import { RectangleToolsAzimuthService } from '../shared/rectangle-tools-azimuth.service';
import { SidesCalculationResult } from '../shared/rectangle-tools-geometry.model';
import { RectangleToolsGeometryService } from '../shared/rectangle-tools-geometry.service';
import { RectangleToolsSaveService } from '../shared/rectangle-tools-save.service';
import { RectangleToolsService } from '../shared/rectangle-tools-service';
import { RectangleToolsUtilsService } from '../shared/rectangle-tools-utls.service';

@Injectable({
  providedIn: 'root',
})
export class RectTubeService extends RectangleToolsService {
  activePart: RECT_TUBE_PART = RECT_TUBE_PART.NONE;
  highlightedSample: { part: RECT_TUBE_PART; i: number } = {
    part: RECT_TUBE_PART.NONE,
    i: -1,
  };
  samples: CuboidPartsArrays<Mesh> = { 1: [], 2: [], 3: [] };

  constructor(
    private rectangleToolsUtilsService: RectangleToolsUtilsService,
    private utilsService: UtilsService,
    private rectangleToolsSaveService: RectangleToolsSaveService,
    private rectangleToolsAzimuthService: RectangleToolsAzimuthService,
    private babylonNodesService: BabylonNodesService,
    private geometryService: RectangleToolsGeometryService,
  ) {
    super(
      rectangleToolsUtilsService,
      utilsService,
      rectangleToolsSaveService,
      rectangleToolsAzimuthService,
    );
  }

  generateRectTube() {
    try {
      const result = this.geometryService.calculateSidesData(
        this.samples[RECT_TUBE_PART.SURFACE],
        this.samples[RECT_TUBE_PART.ADJACENT_SURFACE],
      );
      const thickness = this.calculateThickness(result);
      this.save(
        LAYER_TYPES.RECT_TUBE,
        result.topPlaneNormal,
        result.originalSurfacePlaneNormal,
        result.centerPosition,
        result.adjacentSurfaceLength,
        result.originalSurfaceLength,
        result.topBottomSurfaceLength,
        this.samples[RECT_TUBE_PART.ADJACENT_SURFACE],
        thickness,
      );
      this.clearSamples();
    } catch (err) {
      this.clearSamples();
      this.utilsService.catchToolCreationError(err);
    }
  }

  setActivePart(part: RECT_TUBE_PART) {
    this.activePart = this.activePart === part ? RECT_TUBE_PART.NONE : part;
    this._dataChange$.next();
  }

  highlightSample(part: RECT_TUBE_STRICT, i: number) {
    this.highlightSamplePoint(part, i);
    this._dataChange$.next();
  }

  deleteSample(part: RECT_TUBE_STRICT, i: number) {
    this.deleteSamplePoint(part, i);
    this._dataChange$.next();
  }

  clearSamples() {
    this.clearSamplesPoints();
    this._dataChange$.next();
  }

  private calculateThickness(sidesData: SidesCalculationResult): number {
    const options = {
      height: sidesData.topBottomSurfaceLength,
      depth: sidesData.originalSurfaceLength,
      width: sidesData.adjacentSurfaceLength,
    };

    const box = this.babylonNodesService.createBox(
      sidesData.centerPosition.clone(),
      options,
      Quaternion.FromRotationMatrix(
        Matrix.LookDirectionLH(
          sidesData.originalSurfacePlaneNormal.negate(),
          sidesData.topPlaneNormal.negate(),
        ),
      ),
      'box',
    );

    box.bakeCurrentTransformIntoVertices(true);
    const vertices = this.rectangleToolsUtilsService.extractVertices(box);
    box.dispose();

    const FACE_STRIDE = 4;
    const FACE_COUNT = 4;
    const planes = Array.from({ length: FACE_COUNT }, (_, faceIndex) => {
      const offset = faceIndex * FACE_STRIDE;
      return Plane.FromPoints(vertices[offset], vertices[offset + 1], vertices[offset + 2]);
    });

    const distances = this.samples[RECT_TUBE_PART.INNER].map((point) =>
      Math.min(...planes.map((plane) => Math.abs(plane.signedDistanceTo(point.position)))),
    );
    return median(distances) || 0.01;
  }
}
